import {AzureLayer, getLayerFilter} from "@esWebAPI/es/map/layer/azure/AzureLayer";
import {ILayerView} from "@esWebAPI/es/model/Layer";
import { ColorThematic } from "@esWebAPI/es/model/Thematic";
import {layer, Map, PolygonLayerOptions, source} from "azure-maps-control";
import {DatasourceManager} from "@esWebAPI/es/map/module/DatasourceManager";
import {ColorThematicStyler} from "@esWebAPI/es/map/styler/ColorThematicStyler";
import {DataDrivenPropertyValueSpecification, ExpressionFilterSpecification} from "@maplibre/maplibre-gl-style-spec";


export class AzurePolygonLayer implements AzureLayer {

    private readonly _map: Map;
    private readonly datasourceManager: DatasourceManager
    private readonly colorStyler: ColorThematicStyler;

    private _layerView: ILayerView;

    private datasourceId: string|undefined;
    private _layer: layer.PolygonLayer|undefined;
    private colorThematic: ColorThematic|undefined;

    private layerFilter: ExpressionFilterSpecification|undefined;

    constructor(map: Map, layerView: ILayerView, datasourceManager: DatasourceManager, colorStyler: ColorThematicStyler, layerFilter?: ExpressionFilterSpecification) {
        this._map = map;
        this._layerView = layerView;
        this.datasourceManager = datasourceManager;
        this.colorStyler = colorStyler;
        this.layerFilter = layerFilter;
    }

    private async updateThematics(): Promise<void> {
        this.colorThematic = await this.colorStyler.getThematic(this._layerView);
    }

    private getOptions() {

        const fillColor = this.colorStyler.getFillHex(this._layerView, this.colorThematic);
        let fillOpacity = this.colorStyler.getFillOpacity(this._layerView);

        if (this.colorThematic && this._layerView.styleSettings.colorThematicSettings?.enabled) {
            fillOpacity = fillOpacity || 0.5
        }

        return {
            sourceLayer: this.datasourceId && this.datasourceManager.isPbf(this.datasourceId) ? 'es' : undefined,
            fillColor,
            fillOpacity,
            visible: this._layerView.enabled,
            filter: getLayerFilter(this._layerView, this.layerFilter)
        } as PolygonLayerOptions;
    }

    public async create(datasource: source.Source, colorThematic?: ColorThematic): Promise<void> {
        this.datasourceId = datasource.getId();
        this.colorThematic = colorThematic;
        this._layer = new layer.PolygonLayer(datasource.getId(), this.getLayerId(), this.getOptions());
        this._map.layers.add(this._layer);
    }

    public async recreate(layerView: ILayerView, datasource: source.Source, colorThematic?: ColorThematic) {
        this._layerView = layerView;
        return this.create(datasource, colorThematic)
    }

    public async update(layerView: ILayerView): Promise<void> {
        this._layerView = layerView;
        await this.updateThematics();
        let options = this.getOptions();
        this._layer?.setOptions(options);
    }

    public setLayerFilter(filter?: ExpressionFilterSpecification) {
        this.layerFilter = filter;
    }

    public setOptions(options: PolygonLayerOptions) {
        this._layer?.setOptions(options);
    }

    public remove(): void {
        if (this._layer && this._map.layers.getLayerById(this._layer.getId())) {
            this._map.layers.remove(this._layer.getId());
        }
        this._layer = undefined;
    }

    public hide(): void {
        this._layer?.setOptions({visible: false});
    }

    public show(): void {
        this._layer?.setOptions({visible: true});
    }

    public hideFeature(featureId: number): void {
        const fillOpacity = this.colorStyler.getFillOpacity(this._layerView) ?? 1;
        const opacity = ['case', ['==', ['id'], featureId], 0, fillOpacity] as DataDrivenPropertyValueSpecification<number>;
        this._layer?.setOptions({fillOpacity: opacity});
    }

    public showFeatures(): void {
        let fillOpacity = this.colorStyler.getFillOpacity(this._layerView);
        if (this.colorThematic && this._layerView.styleSettings.colorThematicSettings?.enabled) {
            fillOpacity = fillOpacity || 0.5
        }

        this._layer?.setOptions({fillOpacity: fillOpacity});
    }

    public getLayerId(): string {
        return "polygon_" + this._layerView.id;
    }

}